import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DollarSign, Download, Receipt, Users, CheckCircle2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TIPO_LABEL = { "12_meses": "12 meses", mensal: "Mensal", renovacao: "Renovação", venda: "Venda" };
const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    if (iso.length === 10) return new Date(iso + "T12:00:00").toLocaleDateString("pt-BR");
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch { return iso; }
};

export default function FinanceiroComercial() {
  const hoje = new Date();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("contrato");
  const fileInputsRef = useRef({});
  const [contratos, setContratos] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [equipe, setEquipe] = useState(null);

  const carregar = async () => {
    const params = {};
    if (filtroStatus) params.status_pagamento = filtroStatus;
    if (filtroTipo) params.tipo = filtroTipo;
    const { data } = await api.get("/financeiro/comercial/contratos", { params });
    setContratos(data.items || []);
  };
  const carregarEquipe = async () => {
    const { data } = await api.get("/comercial/comissoes/equipe", { params: { mes, ano } });
    setEquipe(data);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [filtroStatus, filtroTipo]);
  useEffect(() => { carregarEquipe(); /* eslint-disable-next-line */ }, [mes, ano]);

  const marcarPago = async (cb_id) => {
    try {
      await api.post(`/financeiro/comercial/contratos/${cb_id}/marcar-pago`);
      toast.success("Comissão marcada como paga");
      carregar(); carregarEquipe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const lotePagar = async (vendedor_id) => {
    if (!window.confirm("Marcar TODAS as comissões do vendedor neste mês como pagas?")) return;
    try {
      const { data } = await api.post("/financeiro/comercial/comissoes/lote-pagar", { vendedor_id, mes, ano });
      toast.success(`${data.atualizados} comissão(ões) marcadas como pagas`);
      carregar(); carregarEquipe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const baixarDoc = async (cb_id, tipo) => {
    try {
      const resp = await api.get(`/comercial/contratos-bonus/${cb_id}/download/${tipo}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${tipo}_${cb_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha no download"); }
  };

  // AJUSTE 2 — Upload posterior de proposta/contrato assinados
  const uploadAnexoPosterior = async (cb_id, tipo, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Apenas PDF é aceito"); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo maior que 10MB"); return;
    }
    const fd = new FormData();
    fd.append(`${tipo}_pdf`, file);
    try {
      await api.patch(`/comercial/contratos-bonus/${cb_id}/anexos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${tipo === "contrato" ? "Contrato" : "Proposta"} anexado com sucesso`);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao anexar");
    }
  };

  return (
    <div className="space-y-5" data-testid="financeiro-comercial-page">
      <div className="flex items-center gap-3">
        <Receipt size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Financeiro Comercial</h1>
          <p className="text-sm text-muted-foreground">Contratos ativados e gestão de comissões</p>
        </div>
      </div>

      <Tabs defaultValue="contratos">
        <TabsList>
          <TabsTrigger value="contratos" data-testid="tab-contratos">
            <Receipt size={14} className="mr-1" /> Contratos ativados
          </TabsTrigger>
          <TabsTrigger value="comissoes" data-testid="tab-comissoes-equipe">
            <Users size={14} className="mr-1" /> Comissões a pagar (equipe)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contratos">
          <Card className="p-4 mb-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Status pagamento</Label>
                <Select value={filtroStatus || "_all"} onValueChange={(v) => setFiltroStatus(v === "_all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    <SelectItem value="a_receber">A receber</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={filtroTipo || "_all"} onValueChange={(v) => setFiltroTipo(v === "_all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    <SelectItem value="12_meses">12 meses</SelectItem>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="renovacao">Renovação</SelectItem>
                    <SelectItem value="venda">Venda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Valor 1ª Fatura</TableHead>
                  <TableHead className="text-right">Bônus</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Término</TableHead>
                  <TableHead>Docs / Assinatura</TableHead>
                  <TableHead>Contrato</TableHead>
                  <TableHead>Status Pgto</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contratos.map((c) => (
                  <TableRow
                    key={c.id}
                    data-testid={`contrato-row-${c.id}`}
                    className={c.id === highlightId ? "bg-amber-50 border-l-4 border-amber-400" : ""}
                  >
                    <TableCell className="font-medium">{c.cliente_nome}</TableCell>
                    <TableCell className="text-xs">{c.vendedor_nome}</TableCell>
                    <TableCell><Badge variant="outline">{TIPO_LABEL[c.tipo]}</Badge></TableCell>
                    <TableCell className="text-right">{fmtMoney(c.valor_primeira_fatura)}</TableCell>
                    <TableCell className="text-right font-bold" style={{ color: "#1B3A6B" }}>{fmtMoney(c.valor_bonus)}</TableCell>
                    <TableCell>{fmtData(c.data_inicio)}</TableCell>
                    <TableCell>{fmtData(c.data_termino)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          {c.contrato_pdf ? (
                            <Button size="sm" variant="ghost" onClick={() => baixarDoc(c.id, "contrato")} title="Contrato PDF" data-testid={`dl-contrato-${c.id}`}>
                              <Download size={12} className="mr-1" /> C
                            </Button>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept=".pdf"
                                ref={(el) => (fileInputsRef.current[`${c.id}_contrato`] = el)}
                                onChange={(e) => uploadAnexoPosterior(c.id, "contrato", e.target.files?.[0])}
                                className="hidden"
                                data-testid={`upload-contrato-input-${c.id}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fileInputsRef.current[`${c.id}_contrato`]?.click()}
                                title="Anexar contrato assinado depois"
                                data-testid={`upload-contrato-btn-${c.id}`}
                              >
                                <Upload size={12} className="mr-1" /> C
                              </Button>
                            </>
                          )}
                          {c.proposta_pdf ? (
                            <Button size="sm" variant="ghost" onClick={() => baixarDoc(c.id, "proposta")} title="Proposta PDF" data-testid={`dl-proposta-${c.id}`}>
                              <Download size={12} className="mr-1" /> P
                            </Button>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept=".pdf"
                                ref={(el) => (fileInputsRef.current[`${c.id}_proposta`] = el)}
                                onChange={(e) => uploadAnexoPosterior(c.id, "proposta", e.target.files?.[0])}
                                className="hidden"
                                data-testid={`upload-proposta-input-${c.id}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fileInputsRef.current[`${c.id}_proposta`]?.click()}
                                title="Anexar proposta assinada depois"
                                data-testid={`upload-proposta-btn-${c.id}`}
                              >
                                <Upload size={12} className="mr-1" /> P
                              </Button>
                            </>
                          )}
                        </div>
                        {c.signature_status === "pending" && (
                          <Badge
                            className="bg-amber-100 text-amber-800 border border-amber-300 w-fit"
                            data-testid={`badge-pendente-assinatura-${c.id}`}
                          >
                            <AlertTriangle size={10} className="mr-1" /> Pendente de assinatura
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.status === "ativo" ? (
                        <Badge className="bg-emerald-600 text-white">Ativo</Badge>
                      ) : (
                        <Badge variant="outline">{c.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.status_pagamento === "pago" ? (
                        <Badge className="bg-green-600 text-white">✅ Pago</Badge>
                      ) : (
                        <Badge className="bg-blue-600 text-white">A receber</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.status_pagamento !== "pago" && (
                        <Button
                          size="sm"
                          onClick={() => marcarPago(c.id)}
                          disabled={c.status !== "ativo"}
                          title={c.status !== "ativo" ? "Contrato não está ativo" : "Marcar como pago"}
                          className="text-white disabled:opacity-50"
                          style={{ background: c.status === "ativo" ? "#16a34a" : "#94a3b8" }}
                          data-testid={`marcar-pago-${c.id}`}
                        >
                          <CheckCircle2 size={12} className="mr-1" /> Marcar Pago
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {contratos.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center py-6 text-muted-foreground">Nenhum contrato encontrado.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="comissoes">
          <Card className="p-4 mb-3 flex gap-3 items-end">
            <div>
              <Label className="text-xs">Mês</Label>
              <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
                <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ano</Label>
              <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {equipe && (
              <div className="ml-auto flex gap-4 text-sm">
                <div><span className="text-muted-foreground">Total:</span> <strong>{fmtMoney(equipe.total_geral)}</strong></div>
                <div className="text-green-700">Pago: <strong>{fmtMoney(equipe.pago_geral)}</strong></div>
                <div className="text-amber-700">Pendente: <strong>{fmtMoney(equipe.pendente_geral)}</strong></div>
              </div>
            )}
          </Card>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendedor</TableHead>
                  <TableHead className="text-center">Contratos</TableHead>
                  <TableHead className="text-right">Total Bônus</TableHead>
                  <TableHead className="text-right">Pago</TableHead>
                  <TableHead className="text-right">Pendente</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(equipe?.vendedores || []).map((v) => (
                  <TableRow key={v.vendedor_id} data-testid={`vend-com-${v.vendedor_id}`}>
                    <TableCell className="font-medium">{v.vendedor_nome}</TableCell>
                    <TableCell className="text-center">{v.total_contratos}</TableCell>
                    <TableCell className="text-right font-bold">{fmtMoney(v.total_bonus)}</TableCell>
                    <TableCell className="text-right text-green-700">{fmtMoney(v.pago)}</TableCell>
                    <TableCell className="text-right text-amber-700">{fmtMoney(v.pendente)}</TableCell>
                    <TableCell className="text-right">
                      {v.pendente > 0 && (
                        <Button size="sm" onClick={() => lotePagar(v.vendedor_id)} className="text-white" style={{ background: "#16a34a" }} data-testid={`lote-pagar-${v.vendedor_id}`}>
                          <DollarSign size={12} className="mr-1" /> Pagar Lote
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(!equipe || equipe.vendedores.length === 0) && (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Sem comissões no período.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
